import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import config from "../../config";
import { getToken, resetUserSession } from "../../service/AuthService";
import { debounce } from "lodash";
import { useHistory } from "react-router-dom";

// ─── Design tokens (mirrors TradeDashboard) ───────────────────────────────────
const C = {
  bg:     "#08101e",
  bg1:    "#0c1525",
  bg2:    "#101b2e",
  bg3:    "#141f33",
  border: "#1e2d42",
  border2:"#243548",
  text:   "#e8edf5",
  body:   "#b8c8dc",
  meta:   "#6a7d95",
  green:  "#00e896",
  cyan:   "#00c8ff",
  amber:  "#ffb340",
  red:    "#ff4466",
  blue:   "#4488ff",
  purple: "#9966ff",
};
const FONT = "'DM Mono', monospace";

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&display=swap');
  * { box-sizing: border-box; }
  .adm-input, .adm-select {
    width: 100%; background: ${C.bg}; border: 1px solid ${C.border};
    color: ${C.text}; font-family: ${FONT}; font-size: 11px;
    padding: 6px 10px; border-radius: 4px; outline: none;
    transition: border-color .12s;
  }
  .adm-input:focus, .adm-select:focus { border-color: ${C.cyan}; }
  .adm-select option { background: ${C.bg1}; }
  .adm-btn {
    font-family: ${FONT}; font-size: 9px; font-weight: 500;
    letter-spacing: .8px; border-radius: 4px; cursor: pointer;
    padding: 7px 16px; border: 1px solid ${C.border};
    background: transparent; color: ${C.body}; transition: all .12s;
  }
  .adm-btn:hover { background: ${C.bg2}; border-color: ${C.border2}; }
  .adm-btn-green  { color: ${C.green};  border-color: ${C.green}30;  background: ${C.green}10;  }
  .adm-btn-green:hover  { background: ${C.green}20; }
  .adm-btn-amber  { color: ${C.amber};  border-color: ${C.amber}30;  background: ${C.amber}10;  }
  .adm-btn-amber:hover  { background: ${C.amber}20; }
  .adm-btn-red    { color: ${C.red};    border-color: ${C.red}30;    background: ${C.red}10;    }
  .adm-btn-red:hover    { background: ${C.red}20; }
  .adm-btn-cyan   { color: ${C.cyan};   border-color: ${C.cyan}30;   background: ${C.cyan}10;   }
  .adm-btn-cyan:hover   { background: ${C.cyan}20; }
  .adm-btn-active { color: ${C.bg}; background: ${C.cyan}; border-color: ${C.cyan}; }
  .adm-btn-active:hover { background: ${C.cyan}cc; }
  .adm-scroll { overflow-y: auto; max-height: 560px; }
  .adm-scroll::-webkit-scrollbar { width: 4px; }
  .adm-scroll::-webkit-scrollbar-track { background: transparent; }
  .adm-scroll::-webkit-scrollbar-thumb { background: ${C.border2}; border-radius: 2px; }
  .adm-row:hover { background: ${C.bg2}; }
  @keyframes orbPulse {
    0%,100% { transform:scale(1); opacity:.15; }
    50%      { transform:scale(3); opacity:0; }
  }
`;

// ─── UI Primitives ─────────────────────────────────────────────────────────────
const Orb = ({ color, size = 8, speed = "3s" }) => (
  <div style={{ position:"relative", width:size, height:size, flexShrink:0 }}>
    <div style={{ position:"absolute", inset:0, borderRadius:"50%", background:color, animation:`orbPulse ${speed} ease-in-out infinite` }} />
    <div style={{ position:"absolute", inset:"25%", borderRadius:"50%", background:color }} />
  </div>
);

const Eyebrow = ({ children }) => (
  <div style={{ fontFamily:FONT, fontSize:9, color:C.meta, letterSpacing:"2px", textTransform:"uppercase", marginBottom:10 }}>
    {children}
  </div>
);

const Chip = ({ color, children }) => (
  <span style={{
    fontFamily:FONT, fontSize:9, fontWeight:500, color,
    background:color+"18", border:`1px solid ${color}30`,
    borderRadius:3, padding:"2px 8px", letterSpacing:".6px", whiteSpace:"nowrap",
  }}>
    {children}
  </span>
);

// ─── Unauth screen ─────────────────────────────────────────────────────────────
const UnauthScreen = ({ onLogin }) => (
  <div style={{ background:C.bg, minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center" }}>
    <style>{CSS}</style>
    <div style={{ background:C.bg1, border:`1px solid ${C.border}`, borderRadius:12, padding:"40px 48px", textAlign:"center", maxWidth:360 }}>
      <div style={{ display:"flex", justifyContent:"center", marginBottom:20 }}>
        <Orb color={C.amber} size={10} speed="2.5s" />
      </div>
      <div style={{ fontFamily:FONT, fontSize:9, color:C.amber, letterSpacing:"1.5px", marginBottom:10 }}>SESSION REQUIRED</div>
      <div style={{ fontFamily:FONT, fontSize:11, color:C.body, marginBottom:8, fontWeight:300 }}>Sign in to access system controls</div>
      <div style={{ fontFamily:FONT, fontSize:10, color:C.meta, marginBottom:28 }}>BerBul AI Trading · Admin</div>
      <button className="adm-btn adm-btn-cyan" onClick={onLogin} style={{ padding:"9px 28px", letterSpacing:"1px" }}>
        GO TO LOGIN
      </button>
    </div>
  </div>
);

// ─── Server offline screen ─────────────────────────────────────────────────────
const ServerOfflineScreen = () => (
  <div style={{ background:C.bg, minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center" }}>
    <style>{CSS}</style>
    <div style={{ background:C.bg1, border:`1px solid ${C.border}`, borderRadius:12, padding:"40px 48px", textAlign:"center", maxWidth:420 }}>
      <div style={{ display:"flex", justifyContent:"center", marginBottom:20 }}>
        <Orb color={C.meta} size={10} speed="4s" />
      </div>
      <div style={{ fontFamily:FONT, fontSize:9, color:C.meta, letterSpacing:"1.5px", marginBottom:12 }}>
        TRADING SERVER OFFLINE
      </div>
      <div style={{ fontFamily:FONT, fontSize:12, color:C.body, marginBottom:10, fontWeight:300, lineHeight:1.6 }}>
        The trading bot only runs during active market hours.
      </div>
      <div style={{ fontFamily:FONT, fontSize:10, color:C.meta, marginBottom:28, lineHeight:1.6 }}>
        Admin controls will be available when the server starts.<br />
        NYSE market hours: 9:30 AM – 4:00 PM ET
      </div>
      {/* Market hours visual */}
      <div style={{
        background:C.bg, border:`1px solid ${C.border}`, borderRadius:8,
        padding:"12px 20px", display:"flex", justifyContent:"space-between", alignItems:"center",
      }}>
        {[
          { label:"OPEN",  time:"9:30 AM",  color:C.green },
          { label:"CLOSE", time:"4:00 PM",  color:C.red   },
        ].map(({ label, time, color }) => (
          <div key={label} style={{ textAlign:"center" }}>
            <div style={{ fontFamily:FONT, fontSize:8, color:C.meta, letterSpacing:"1px", marginBottom:4 }}>{label}</div>
            <div style={{ fontFamily:FONT, fontSize:12, color, fontWeight:500 }}>{time}</div>
            <div style={{ fontFamily:FONT, fontSize:8, color:C.meta }}>ET</div>
          </div>
        ))}
        <div style={{ fontFamily:FONT, fontSize:8, color:C.meta, letterSpacing:".5px" }}>
          NYSE · MON–FRI
        </div>
      </div>
    </div>
  </div>
);

// ─── CONFIG SCHEMA ─────────────────────────────────────────────────────────────
const CONFIG_SCHEMA = {
  trader: {
    title: "TRADER CONTROLS",
    description: "Position sizing, risk limits, and market timing",
    color: C.green,
    fields: {
      symbol:          { label: "Symbol",               type: "text",   category: "Core"      },
      qty_per_trade:   { label: "Qty Per Trade",         type: "number", category: "Core"      },
      max_position:    { label: "Max Position Size",     type: "number", category: "Core"      },
      max_orders:      { label: "Max Concurrent Orders", type: "number", category: "Core"      },
      stop_loss_long:  { label: "Stop Loss (Long)",      type: "number", category: "Risk"      },
      stop_loss_short: { label: "Stop Loss (Short)",     type: "number", category: "Risk"      },
      min_profit:      { label: "Min Profit Target",     type: "number", category: "Risk"      },
      start_of_trade:  { label: "Trading Start",         type: "time",   category: "Timing"    },
      end_of_trade:    { label: "Trading End",           type: "time",   category: "Timing"    },
      out_of_market:   { label: "Market Exit",           type: "time",   category: "Timing"    },
      order_type:      { label: "Order Type",            type: "select", options: ["limit","market"], category: "Execution" },
      order_wait:      { label: "Order Wait",            type: "text",   category: "Execution" },
      buffer:          { label: "Price Buffer",          type: "number", category: "Execution" },
    }
  },
  data_scientist: {
    title: "AI / ML CONFIG",
    description: "Model selection, prediction parameters, and thresholds",
    color: C.purple,
    fields: {
      model:              { label: "ML Model",           type: "text",   category: "Model"      },
      model_key:          { label: "Model Version",      type: "text",   category: "Model"      },
      gen_ai_model_vendor:{ label: "Gen AI Vendor",      type: "select", options: ["google","openai","anthropic"], category: "Model" },
      gen_ai_model_id:    { label: "Gen AI Model ID",    type: "text",   category: "Model"      },
      predict_input:      { label: "Prediction Input",   type: "select", options: ["minute_bar","tick","quote"], category: "Prediction" },
      gen_ai_pred_len:    { label: "Prediction Length",  type: "number", category: "Prediction" },
      gen_ai_seq_len:     { label: "Sequence Length",    type: "number", category: "Prediction" },
      gen_ai_top_k:       { label: "Top K",              type: "number", category: "Prediction" },
      gen_ai_conf_thresh: { label: "Confidence Thresh",  type: "number", category: "Thresholds" },
      rsi_th:             { label: "RSI Threshold",      type: "number", category: "Thresholds" },
      hold_threshold:     { label: "Hold Threshold",     type: "number", category: "Thresholds" },
      min_bars:           { label: "Min Bars",           type: "number", category: "Features"   },
      pressure_window:    { label: "Pressure Window",    type: "number", category: "Features"   },
      water_mark_window:  { label: "Watermark Window",   type: "number", category: "Features"   },
    }
  },
  engineer: {
    title: "SYSTEM CONFIG",
    description: "API endpoints, system behavior, and technical settings",
    color: C.cyan,
    fields: {
      play:                        { label: "Bot Status",         type: "select",   options: ["active","pause"],          category: "Control"      },
      mode:                        { label: "Trading Mode",       type: "select",   options: ["both","long","short"],     category: "Control"      },
      start_mode:                  { label: "Start Mode",         type: "select",   options: ["auto","manual"],           category: "Control"      },
      drl_url:                     { label: "DRL URL",            type: "text",     category: "DRL API"     },
      drl_api_key:                 { label: "DRL API Key",        type: "password", category: "DRL API"     },
      apply_drl:                   { label: "Apply DRL",          type: "checkbox", category: "DRL API"     },
      prediction_url:              { label: "Prediction URL",     type: "text",     category: "Predict API" },
      prediction_api_key:          { label: "Prediction Key",     type: "password", category: "Predict API" },
      trade_outcome_url:           { label: "Outcome URL",        type: "text",     category: "Outcome API" },
      trade_outcome_api_key:       { label: "Outcome Key",        type: "password", category: "Outcome API" },
      trade_outcome_enabled:       { label: "Enable Tracking",    type: "checkbox", category: "Outcome API" },
      trade_outcome_retry_attempts:{ label: "Retry Attempts",     type: "number",   category: "Outcome API" },
      trade_outcome_batch_size:    { label: "Batch Size",         type: "number",   category: "Outcome API" },
      stale_window:                { label: "Stale Window",       type: "text",     category: "System"      },
      trade_pub:                   { label: "Trade Publishing",   type: "checkbox", category: "System"      },
      publish_c2:                  { label: "Publish to C2",      type: "checkbox", category: "System"      },
    }
  },
  evaluation_agent: {
    title: "EVAL AGENT",
    description: "AI-powered position evaluation and monitoring",
    color: C.amber,
    fields: {
      eval_agent_url:                  { label: "Eval URL",            type: "text",     category: "API"      },
      eval_agent_api_key:              { label: "Eval API Key",        type: "password", category: "API"      },
      eval_agent_interval_minutes:     { label: "Interval (min)",      type: "number",   category: "Timing"   },
      eval_agent_min_interval_minutes: { label: "Min Interval (min)",  type: "number",   category: "Timing"   },
      eval_agent_max_interval_minutes: { label: "Max Interval (min)",  type: "number",   category: "Timing"   },
      eval_agent_emergency_conditions: { label: "Emergency Conditions",type: "checkbox", category: "Behavior" },
    }
  }
};

// ─── ConfigCard ─────────────────────────────────────────────────────────────────
const ConfigCard = ({ schema, config: cfg, onUpdate }) => {
  const categorized = useMemo(() => {
    const cats = {};
    Object.entries(schema.fields).forEach(([key, field]) => {
      const cat = field.category || "Other";
      if (!cats[cat]) cats[cat] = [];
      cats[cat].push({ key, ...field });
    });
    return cats;
  }, [schema.fields]);

  const handleChange = (key, value, type) => {
    let v = value;
    if (type === "checkbox") v = value === "true" || value === true;
    onUpdate(key, v);
  };

  const renderField = (key, field) => {
    const value = cfg[key] !== undefined ? cfg[key] : "";

    if (field.type === "checkbox") {
      const checked = value === true || value === "true";
      return (
        <div
          onClick={() => handleChange(key, !checked, "checkbox")}
          style={{
            width: 36, height: 20, borderRadius: 10, cursor: "pointer",
            background: checked ? C.cyan + "40" : C.bg3,
            border: `1px solid ${checked ? C.cyan : C.border}`,
            position: "relative", transition: "all .15s",
          }}
        >
          <div style={{
            position: "absolute", top: 3,
            left: checked ? 18 : 3,
            width: 12, height: 12, borderRadius: "50%",
            background: checked ? C.cyan : C.meta,
            transition: "left .15s",
          }} />
        </div>
      );
    }

    if (field.type === "select") {
      return (
        <select
          className="adm-select"
          value={value}
          onChange={e => handleChange(key, e.target.value, field.type)}
        >
          {field.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
    }

    return (
      <input
        className="adm-input"
        type={field.type === "password" ? "password" : field.type === "time" ? "time" : field.type === "number" ? "number" : "text"}
        value={value}
        onChange={e => handleChange(key, e.target.value, field.type)}
      />
    );
  };

  const fieldCount = Object.keys(schema.fields).length;

  return (
    <div style={{ background:C.bg1, border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden", height:"100%" }}>
      {/* Card header */}
      <div style={{ padding:"16px 20px 14px", borderBottom:`1px solid ${C.border}`, background:C.bg }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
          <span style={{ fontFamily:FONT, fontSize:10, fontWeight:500, color:schema.color, letterSpacing:"1.2px" }}>
            {schema.title}
          </span>
          <Chip color={schema.color}>{fieldCount} FIELDS</Chip>
        </div>
        <div style={{ fontFamily:FONT, fontSize:9, color:C.meta, fontWeight:300 }}>
          {schema.description}
        </div>
      </div>

      {/* Scrollable fields */}
      <div className="adm-scroll" style={{ padding:"14px 20px" }}>
        {Object.entries(categorized).map(([category, fields]) => (
          <div key={category} style={{ marginBottom:16 }}>
            {/* Category eyebrow */}
            <div style={{
              fontFamily:FONT, fontSize:8, color:schema.color, letterSpacing:"1.5px",
              textTransform:"uppercase", marginBottom:8, paddingBottom:4,
              borderBottom:`1px solid ${schema.color}18`,
            }}>
              {category}
            </div>
            {fields.map(field => (
              <div
                key={field.key}
                className="adm-row"
                style={{
                  display:"grid", gridTemplateColumns:"1fr 1fr",
                  gap:8, alignItems:"center",
                  padding:"5px 6px", borderRadius:4, marginBottom:2,
                }}
              >
                <span style={{ fontFamily:FONT, fontSize:10, color:C.body }}>{field.label}</span>
                <div>{renderField(field.key, field)}</div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── MAIN ADMIN PAGE ───────────────────────────────────────────────────────────
function AdminPage() {
  const history       = useHistory();
  const [authed,      setAuthed]      = useState(null);   // null=checking, true=ok, false=no token
  const [serverUp,    setServerUp]    = useState(null);   // null=checking, true=up, false=down
  const [isTrading,   setIsTrading]   = useState(false);
  const [isPaused,    setIsPaused]    = useState(false);
  const [errorMsg,    setErrorMsg]    = useState(null);
  const [activePersona, setActivePersona] = useState("trader");
  const [cfg,         setCfg]         = useState({});
  const debounceDelay = 500;
  const APIUrl = config.API_SOCKET_URL;

  // ── Auth gate ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const token = getToken();
    if (!token) {
      setAuthed(false);
    } else {
      setAuthed(true);
    }
  }, []);

  // ── Load config once authed ─────────────────────────────────────────────────
  useEffect(() => {
    if (!authed) return;
    axios.get(APIUrl + "/trade/config", {
      headers: { "x-api-key": config.API_KEY, authorizationToken: getToken() },
    })
      .then(res => { setServerUp(true); setCfg(res.data.msg); })
      .catch(err => {
        const status = err.response?.status;
        console.error("[Admin] Config load failed:", status, err.message);
        if (status === 401 || status === 403) {
          // Token is invalid — clear session and ask to log in
          resetUserSession();
          setAuthed(false);
        } else {
          // Network error, 5xx, timeout — server is simply not running
          setServerUp(false);
        }
      });
  }, [authed]);

  // ── API helpers ─────────────────────────────────────────────────────────────
  async function callBackendAPI(resource, msg) {
    try {
      const res = await axios.post(APIUrl + "/" + resource, msg, {
        headers: { "x-api-key": config.API_KEY, authorizationToken: getToken() },
      });
      return res;
    } catch (err) {
      console.error("[Admin] API call failed:", err.response?.status, err.message);
      // Treat auth errors (401/403) and missing token as session expired
      if (!getToken() || err.response?.status === 401 || err.response?.status === 403) {
        resetUserSession();
        setAuthed(false);
      } else {
        setErrorMsg("API error — " + (err.response?.status || "network"));
      }
      return null;
    }
  }

  const putBackendAPI = (msg) => {
    axios.put(APIUrl + "/trade/config", msg, {
      headers: { "x-api-key": config.API_KEY, authorizationToken: getToken() },
    }).catch(console.error);
  };

  const debouncedUpdate = debounce((key, value) => {
    setCfg(prev => {
      const next = { ...prev, [key]: value };
      putBackendAPI({ config: next });
      return next;
    });
  }, debounceDelay);

  const handleConfigUpdate = (key, value) => {
    setCfg(prev => ({ ...prev, [key]: value }));
    debouncedUpdate(key, value);
  };

  // ── Trading controls ────────────────────────────────────────────────────────
  const startTrading = async () => {
    const res = await callBackendAPI("trade/action", { action: "start" });
    if (res?.status === 200) { setIsTrading(true); setErrorMsg(null); }
    else setErrorMsg("Failed to start bot");
  };
  const stopTrading = async () => {
    const res = await callBackendAPI("trade/action", { action: "stop" });
    if (res?.status === 200) { setIsTrading(false); setIsPaused(false); setErrorMsg(null); }
    else setErrorMsg("Failed to stop bot");
  };
  const pauseTrading = () => {
    setIsPaused(true);
    setCfg(prev => { const n = { ...prev, play:"pause" }; putBackendAPI({ config:n }); return n; });
  };
  const resumeTrading = () => {
    setIsPaused(false);
    setCfg(prev => { const n = { ...prev, play:"active" }; putBackendAPI({ config:n }); return n; });
  };

  // ── Auth redirect ───────────────────────────────────────────────────────────
  if (authed === false) return <UnauthScreen onLogin={() => history.push("/Login")} />;
  if (authed === null || serverUp === null) return (
    <div style={{ background:C.bg, minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <style>{CSS}</style>
      <Orb color={C.cyan} size={12} speed="1.5s" />
    </div>
  );
  if (serverUp === false) return <ServerOfflineScreen />;

  // ── Status colors ───────────────────────────────────────────────────────────
  const statusColor = !isTrading ? C.red : isPaused ? C.amber : C.green;
  const statusLabel = !isTrading ? "STOPPED" : isPaused ? "PAUSED" : "ACTIVE";

  // ── Persona tabs ────────────────────────────────────────────────────────────
  const personas = [
    { key: "trader",          label: "TRADER"    },
    { key: "data_scientist",  label: "AI / ML"   },
    { key: "engineer",        label: "ENGINEER"  },
    { key: "all",             label: "ALL"       },
  ];

  // ── Which cards to show ─────────────────────────────────────────────────────
  const showAll = activePersona === "all";
  const activeSchema = !showAll ? CONFIG_SCHEMA[activePersona] : null;

  return (
    <div style={{ background:C.bg, minHeight:"100vh", fontFamily:FONT }}>
      <style>{CSS}</style>

      {/* ── Status bar ─────────────────────────────────────────────────────── */}
      <div style={{
        background:C.bg1, borderBottom:`1px solid ${C.border}`,
        padding:"10px 32px", display:"flex", justifyContent:"space-between", alignItems:"center",
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <Orb color={statusColor} size={7} speed={isTrading && !isPaused ? "1.5s" : "3s"} />
          <span style={{ fontFamily:FONT, fontSize:9, color:C.body, letterSpacing:".5px" }}>
            TRADING SYSTEM CONTROL
          </span>
          <Chip color={statusColor}>{statusLabel}</Chip>
        </div>

        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          {isTrading ? (
            <>
              {isPaused
                ? <button className="adm-btn adm-btn-green" onClick={resumeTrading}>RESUME</button>
                : <button className="adm-btn adm-btn-amber" onClick={pauseTrading}>PAUSE</button>
              }
              <button className="adm-btn adm-btn-red" onClick={stopTrading}>STOP</button>
            </>
          ) : (
            <button className="adm-btn adm-btn-green" onClick={startTrading}>START BOT</button>
          )}
        </div>
      </div>

      {/* ── Error message ──────────────────────────────────────────────────── */}
      {errorMsg && (
        <div style={{
          background:C.red+"12", borderBottom:`1px solid ${C.red}30`,
          padding:"8px 32px", display:"flex", alignItems:"center", gap:8,
        }}>
          <div style={{ width:5, height:5, borderRadius:"50%", background:C.red }} />
          <span style={{ fontFamily:FONT, fontSize:9, color:C.red, letterSpacing:".4px" }}>{errorMsg}</span>
          <button
            onClick={() => setErrorMsg(null)}
            style={{ marginLeft:"auto", background:"transparent", border:"none", color:C.meta, cursor:"pointer", fontFamily:FONT, fontSize:10 }}
          >
            ✕
          </button>
        </div>
      )}

      <div style={{ maxWidth:1200, margin:"0 auto", padding:"28px 32px 80px" }}>

        {/* ── Page header ──────────────────────────────────────────────────── */}
        <div style={{ marginBottom:24 }}>
          <Eyebrow>Admin</Eyebrow>
          <div style={{ fontFamily:FONT, fontWeight:500, fontSize:"clamp(16px,1.8vw,22px)", color:C.text }}>
            System Configuration
          </div>
          <div style={{ fontFamily:FONT, fontSize:10, color:C.meta, marginTop:4, fontWeight:300 }}>
            Bot parameters · API endpoints · evaluation agent · changes save automatically
          </div>
        </div>

        {/* ── Persona tabs ─────────────────────────────────────────────────── */}
        <div style={{
          display:"flex", gap:4, marginBottom:24,
          background:C.bg1, border:`1px solid ${C.border}`,
          borderRadius:8, padding:4, width:"fit-content",
        }}>
          {personas.map(p => (
            <button
              key={p.key}
              className={`adm-btn ${activePersona === p.key ? "adm-btn-active" : ""}`}
              onClick={() => setActivePersona(p.key)}
              style={{ padding:"6px 18px", letterSpacing:".8px" }}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* ── Config cards ─────────────────────────────────────────────────── */}
        {showAll ? (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
            {Object.entries(CONFIG_SCHEMA).map(([key, schema]) => (
              <ConfigCard key={key} schema={schema} config={cfg} onUpdate={handleConfigUpdate} />
            ))}
          </div>
        ) : (
          <div style={{ display:"grid", gridTemplateColumns: activePersona === "engineer" ? "1fr" : "1fr 1fr", gap:16 }}>
            <ConfigCard schema={activeSchema} config={cfg} onUpdate={handleConfigUpdate} />
            {activePersona !== "engineer" && (
              <ConfigCard schema={CONFIG_SCHEMA.evaluation_agent} config={cfg} onUpdate={handleConfigUpdate} />
            )}
          </div>
        )}

      </div>
    </div>
  );
}

export default AdminPage;